import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useLeadFormStore } from '../../../store/leadFormStore';
import { useAuth } from '../../../contexts/AuthContext';
import * as LeadService from '../../../services/lead.service';
import { StepProps } from '../types';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../config/firebase';
import { Timestamp } from 'firebase/firestore';
import { projectService } from '../../../services/project.service';
import { appointmentService, SlotFullError } from '../../../services/appointment.service';
import { scheduleService } from '../../../services/schedule.service';
import { emailService } from '../../../services/email.service';

const ReviewStep: React.FC<StepProps> = ({ onNext, onBack }) => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const store = useLeadFormStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
      
  const submitLead = async () => {
    if (isSubmitting) return;
    // log to check project id
    if (!store.projectId) {
      toast.error('Please select a project');
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (!currentUser) {
        toast.error('Please log in to create a lead');
        return;
      }

      // Get form data from store
      const formData = store.formData;
      const parentInfo = formData.parent || {};
      const address = formData.address || {};
      const children = formData.children || [];

      // Resolve school segment (campus) info once, reused for lead + email.
      const leadProject = store.projectId ? await projectService.getProject(store.projectId) : null;
      const projectSegmentsList = leadProject?.schoolSegments || [];
      const segmentName = (id?: string) => projectSegmentsList.find(s => s.id === id)?.name || '';

      // Upload images to Firebase Storage
      let uploadedDocs = {
        interestForm: '',
        confirmationText: '',
        misc: ''
      };

      try {
        // Upload Interest Form
        if (formData.documents?.interestForm?.[0]?.url) {
          const interestFormUrl = formData.documents.interestForm[0].url;
          if (interestFormUrl.startsWith('data:')) {
            const timestamp = Date.now();
            const interestFormRef = ref(storage, `leads/${currentUser.uid}/interest_form_${timestamp}.jpg`);
            await uploadString(interestFormRef, interestFormUrl, 'data_url');
            uploadedDocs.interestForm = await getDownloadURL(interestFormRef);
          }
        }

        // Upload Screenshot
        if (formData.documents?.screenshot?.[0]?.url) {
          const screenshotUrl = formData.documents.screenshot[0].url;
          if (screenshotUrl.startsWith('data:')) {
            const timestamp = Date.now();
            const screenshotRef = ref(storage, `leads/${currentUser.uid}/screenshot_${timestamp}.jpg`);
            await uploadString(screenshotRef, screenshotUrl, 'data_url');
            uploadedDocs.confirmationText = await getDownloadURL(screenshotRef);
          }
        }

        // Upload Other
        if (formData.documents?.other?.[0]?.url) {
          const otherUrl = formData.documents.other[0].url;
          if (otherUrl.startsWith('data:')) {
            const timestamp = Date.now();
            const otherRef = ref(storage, `leads/${currentUser.uid}/other_${timestamp}.jpg`);
            await uploadString(otherRef, otherUrl, 'data_url');
            uploadedDocs.misc = await getDownloadURL(otherRef);
          }
        }
      } catch (uploadError) {
        console.error('Error uploading images:', uploadError);
        toast.error('Failed to upload images. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // userProfile uses a single `name` field — there is no firstName/lastName.
      const capturerName = userProfile?.name?.trim() || userProfile?.email || 'Field User';

      const leadData = {
        projectId: store.projectId || "",
        submittedBy: currentUser.uid,
        parentName: `${parentInfo.firstName || ''} ${parentInfo.lastName || ''}`.trim(),
        email: parentInfo.email || '',
        phone: parentInfo.phone || '',
        address: address.street || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zipCode || '',
        children: children.map((child: any) => ({
          name: `${child.firstName || ''} ${child.lastName || ''}`.trim(),
          dateOfBirth: child.dateOfBirth || '',
          grade: child.grade || '',
          school: child.currentSchool || child.school || '',
          ...(child.schoolSegmentId ? { schoolSegmentId: child.schoolSegmentId } : {})
        })),
        ...(formData.schoolSegmentId
          ? { schoolSegmentId: formData.schoolSegmentId, schoolSegmentName: segmentName(formData.schoolSegmentId) }
          : {}),
        status: (formData.initialStatus || 'uncontacted') as any,
        determination: (formData.initialDetermination || 'pending') as any,
        preferredContactMethod: (parentInfo.preferredContact || 'phone') as 'phone' | 'email' | 'text',
        ...(parentInfo.language ? { language: parentInfo.language } : {}),
        notes: formData.notes || '',
        createdAt: Timestamp.now(),
        lastUpdated: Timestamp.now(),
        createdBy: currentUser.uid,
        capturedBy: currentUser.uid,
        capturedByName: capturerName,
        source: capturerName,
        documents: uploadedDocs
      };

      const leadId = await LeadService.createLead(leadData);
      
      // Create appointments if schedule preferences were selected
      if (formData.schedule?.preferredDates?.length > 0 && formData.schedule?.preferredTimes?.length > 0) {
        try {
          // Look up the relevant schedule once so we can pass slotCapacity
          // into createAppointment for capacity enforcement.
          const projectSchedules = store.projectId
            ? await scheduleService.getSchedulesByProject(store.projectId)
            : [];
          const scheduleOwnerId = formData.schedule.selectedClientId || currentUser.uid;
          const ownerSchedule = projectSchedules.find(s => s.userId === scheduleOwnerId);
          const slotCapacity = ownerSchedule?.defaultSlotCapacity ?? 1;

          let appointmentDetails = ''; // Collect appointment info for lead notes

          for (const date of formData.schedule.preferredDates) {
            for (const timeSlot of formData.schedule.preferredTimes) {
              // Extract time from the slot ID
              // Format: date-slotId-startTime-endTime|userId
              let startTime = '09:00'; // default
              let endTime = '10:00'; // default
              
              if (timeSlot.includes('|')) {
                const [timePart, userIdPart] = timeSlot.split('|');
                const parts = timePart.split('-');
                
                // Expected format: date-slotId-startTime-endTime|userId
                // Example: 2025-07-20-abc123-09:00-09:30|userId
                if (parts.length >= 5) {
                  // If we have all parts including times
                  startTime = parts[parts.length - 2]; // Second to last is startTime
                  endTime = parts[parts.length - 1];   // Last is endTime
                } else if (parts.length === 4) {
                  // Format might be: date-startTime-endTime-slotId
                  startTime = parts[1];
                  endTime = parts[2];
                }
              } else {
                // Fallback for old format without userId
                const parts = timeSlot.split('-');
                if (parts.length >= 4) {
                  startTime = parts[parts.length - 2];
                  endTime = parts[parts.length - 1];
                }
              }
              
              // endTime already extracted from slot

              const appointmentData = {
                projectId: store.projectId || '',
                scheduleId: `${formData.schedule.selectedClientId || currentUser.uid}-${store.projectId}`,
                leadId: leadId,
                userId: formData.schedule.selectedClientId || currentUser.uid,
                date: date,
                startTime: startTime,
                endTime: endTime,
                eventType: (formData.schedule.interestedInTour ? 'tour' : 'phone') as 'tour' | 'phone' | 'meeting',
                status: 'scheduled' as const,
                leadName: `${parentInfo.firstName || ''} ${parentInfo.lastName || ''}`.trim()
              };

              try {
                await appointmentService.createAppointment(appointmentData, { slotCapacity });
              } catch (err) {
                if (err instanceof SlotFullError) {
                  toast.error(`Slot ${date} ${startTime} is full and was skipped.`);
                  continue;
                }
                throw err;
              }
              
              // Build appointment details for lead notes
              appointmentDetails += `${formData.schedule.interestedInTour ? 'Tour' : 'Phone call'} scheduled for ${date} at ${startTime}\n`;
            }
          }
          
          // Update lead with appointment info
          if (appointmentDetails) {
            await LeadService.updateLead(leadId, {
              notes: leadData.notes ? `${leadData.notes}\n\nAppointments:\n${appointmentDetails}` : `Appointments:\n${appointmentDetails}`,
              lastUpdated: Timestamp.now()
            });
          }
          
          toast.success('Appointments scheduled successfully!');
        } catch (error) {
          console.error('Error creating appointments:', error);
          toast.error('Lead created but failed to schedule appointments');
        }
      }
      
      // If no appointments were scheduled, send a lead-captured email now.
      // When appointments ARE scheduled, onAppointmentCreated handles the
      // email so we skip here to avoid doubles.
      const hadAppointments = formData.schedule?.preferredDates?.length > 0 && formData.schedule?.preferredTimes?.length > 0;
      if (!hadAppointments && store.projectId) {
        emailService.sendLeadCapturedEmail({
          projectId: store.projectId,
          projectName: leadProject?.name || 'Project',
          leadName: `${parentInfo.firstName || ''} ${parentInfo.lastName || ''}`.trim() || 'New Lead',
          phone: parentInfo.phone,
          email: parentInfo.email,
          address: `${address.street || ''}, ${address.city || ''} ${address.state || ''}`.trim(),
          capturedByName: capturerName,
          leadId,
        }).catch(() => {}); // Non-blocking
      }

      toast.success('Lead created successfully!');

      // Reset form
      if (store.resetForm) {
        store.resetForm();
      }
      
      navigate('/leads');
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to create lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (step: string) => {
    if (store.saveToStorage) {
      store.saveToStorage();
    }
    navigate(`/leads/new?step=${step}`);
  };

  // Get form data with defaults
  const formData = store.formData || {};
  const parentInfo = formData.parent || {};
  const address = formData.address || {};
  const children = formData.children || [];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Review Information</h2>
      {/* Parent Information */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Parent Information</h3>
          <button
            onClick={() => handleEdit('parent')}
            className="text-primary-600 hover:text-primary-700"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="text-sm text-gray-600 space-y-1">
          <p>{parentInfo.firstName} {parentInfo.lastName}</p>
          <p>{parentInfo.email}</p>
          <p>{parentInfo.phone}</p>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Address</h3>
          <button
            onClick={() => handleEdit('address')}
            className="text-primary-600 hover:text-primary-700"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="text-sm text-gray-600">
          <p>{address.street}</p>
          <p>{address.city}, {address.state} {address.zipCode}</p>
        </div>
      </div>

      {/* Children */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Children</h3>
          <button
            onClick={() => handleEdit('children')}
            className="text-primary-600 hover:text-primary-700"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="text-sm text-gray-600 space-y-1">
          {children.map((child: any, index: number) => (
            <p key={index}>
              {child.firstName} {child.lastName || parentInfo.lastName} - Grade {child.grade}
            </p>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => store.setNotes(e.target.value)}
          rows={3}
          placeholder="Add any additional notes about this lead..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
        />
      </div>

      {/* Add our own submit button */}
      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={submitLead}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Lead'}
        </button>
      </div>
    </div>
  );
};

export default ReviewStep;